import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth, type CaptchaFields } from '../lib/auth';
import { Captcha } from '../components/Captcha';
import { SiteFooter } from '../components/Layout';
import { TextInput } from '../components/ui';

export function SignIn() {
  const { signIn, signedOutReason, status } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaFields>({});
  const [captchaError, setCaptchaError] = useState<string | undefined>();
  // Bumped after every attempt: a challenge is single-use, so the one on screen
  // is already spent by the time an error comes back.
  const [captchaToken, setCaptchaToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once the server says this account has two-step verification on. The
  // password stays in state and is sent again with the code: the server has to
  // check both together, and it will not say a code is wanted until the
  // password is right.
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setCaptchaError(undefined);
    try {
      await signIn(email.trim(), password, captcha, code.trim() || undefined);
    } catch (caught) {
      const code_ = caught instanceof ApiError ? caught.code : '';
      const isCaptcha = code_ === 'CAPTCHA_INVALID' || code_ === 'CAPTCHA_REQUIRED';

      if (code_ === 'TOTP_REQUIRED') {
        // First time round: the password was right, and now the code is wanted.
        setNeedsCode(true);
        setCode('');
      } else if (code_ === 'TOTP_INVALID') {
        setNeedsCode(true);
        setCode('');
        setError((caught as ApiError).message);
      } else if (isCaptcha) {
        setCaptchaError((caught as ApiError).message);
      } else {
        // Anything else sends them back to the start, including a lockout: a
        // code field left on screen for an account that is locked is a lie.
        setNeedsCode(false);
        setCode('');
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'Could not sign in. Check your connection and try again.',
        );
      }
      // Either way the challenge is spent; pull a fresh one.
      setCaptchaToken((token) => token + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="row gap-6">
          <div className="brand-mark">K</div>
          <div className="brand-name" style={{ fontSize: 17 }}>
            Kortex HMS
          </div>
        </div>

        <div>
          <h1>One system from the purchase order to the payslip.</h1>
          <p>
            Restaurant billing with GST, stock that depletes itself from recipes, cost-led menu
            pricing, procurement, payroll and the front desk — across every property you run.
          </p>
          <div className="auth-points">
            <div>
              <span>▤</span>
              <span>
                <b>Billing that moves stock.</b> Settling a ticket raises the GST invoice and
                depletes every ingredient behind it.
              </span>
            </div>
            <div>
              <span>⇄</span>
              <span>
                <b>Procurement that reprices.</b> A goods receipt re-weights the moving average and
                updates plate costs.
              </span>
            </div>
            <div>
              <span>₹</span>
              <span>
                <b>Filing-ready GST.</b> B2B, B2CS and HSN summaries export straight out of the
                ledger.
              </span>
            </div>
          </div>
        </div>

        <div className="small" style={{ color: '#7fa9a4' }}>
          Multi-property · role-based access · full audit trail
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <h2>Sign in</h2>
          <div className="sub">Use the account your manager set up for you.</div>

          <form onSubmit={submit} className="card">
            <div className="card-body col gap-16">
              {error ? (
                <div className="form-error">{error}</div>
              ) : signedOutReason ? (
                <div className="form-note">{signedOutReason}</div>
              ) : null}

              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@property.in"
              />
              <TextInput
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {needsCode ? (
                <TextInput
                  label="Authenticator code"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  hint="The six digits from your authenticator app, or one of your recovery codes."
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              ) : null}

              <Captcha
                value={captcha}
                onChange={setCaptcha}
                error={captchaError}
                disabled={busy}
                reloadToken={captchaToken}
              />

              <button className="btn primary lg block" type="submit" disabled={busy}>
                {busy ? 'Signing in…' : needsCode ? 'Verify and sign in' : 'Sign in'}
              </button>

              <div className="center small muted">
                <Link to="/forgot-password">Forgot your password?</Link>
              </div>

              <div className="center small muted">
                New here? <Link to="/register">Register your hotel</Link> — free for 30 days.
              </div>
            </div>
          </form>

          <div className="auth-hint">
            <b>Demo data</b> is seeded with <code>npm run db:seed</code>. Owner:{' '}
            <code>owner@kortexgrand.in</code> / <code>ChangeMe#2026</code>. Staff accounts (
            <code>manager@</code>, <code>cashier@</code>, <code>kitchen@</code>,{' '}
            <code>housekeeping@</code>, <code>stores@</code>, <code>frontdesk@</code>,{' '}
            <code>accounts@kortexgrand.in</code>) share the password <code>Staff#Demo2026</code> and
            each sees only what their role allows.
          </div>
        </div>

        <SiteFooter />
      </main>
    </div>
  );
}
